import { API_BASE_URL } from '@/lib/constants/api';
import {
  hasRefreshToken,
  refreshAccessToken,
} from '@/lib/utils/refreshSession';

interface StoredState {
  state: {
    accessToken: string | null;
    sessionToken: string | null;
    impersonateId: string | null;
    companyId: string | null;
  };
}

/**
 * Error thrown by `apiFetch` for any non-OK response.
 *
 * - `status` — HTTP status code
 * - `body` — parsed JSON body if the server sent JSON, otherwise the raw
 *   response text (or `null` if the body was empty)
 * - `url` — full URL of the failing request
 * - `message` — best human-readable summary the server provided, or a
 *   fallback like `Request failed with status 500`
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  url: string;

  constructor(
    message: string,
    init: { status: number; body: unknown; url: string },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = init.status;
    this.body = init.body;
    this.url = init.url;
  }
}

/**
 * The backend error envelope carries a stable machine-readable `code` alongside
 * the human message (see the Fastify error handler). Read it when a caller needs
 * to branch on *which* failure happened rather than show the raw message.
 * Returns `null` for anything that is not an `ApiError` with a string code.
 */
export function readApiErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body;
  if (typeof body !== 'object' || body === null) return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

export function getCompanyId(): string | null {
  try {
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return null;
    const { state } = JSON.parse(raw) as {
      state: { companyId: string | null };
    };
    return state.companyId ?? null;
  } catch {
    return null;
  }
}

/** Id of the user currently being impersonated, or `null` when not impersonating. */
export function getImpersonateId(): string | null {
  try {
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return null;
    const { state } = JSON.parse(raw) as StoredState;
    return state.impersonateId ?? null;
  } catch {
    return null;
  }
}

/**
 * `asOperator` drops the impersonation and the company switch, so the request
 * is the signed-in Retraced operator's own. For staff-only endpoints, which
 * judge the operator: inside an impersonated session they would otherwise see
 * the customer and refuse.
 */
export function getAuthHeaders({
  asOperator = false,
} = {}): Record<string, string> {
  try {
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return {};
    const { state } = JSON.parse(raw) as StoredState;
    const headers: Record<string, string> = {};
    if (state.accessToken)
      headers.Authorization = `Bearer ${state.accessToken}`;
    if (state.sessionToken) headers['X-Session-Token'] = state.sessionToken;
    if (asOperator) return headers;
    if (state.impersonateId)
      headers['x-retraced-impersonate'] = state.impersonateId;
    if (state.companyId) headers['x-retraced-company-id'] = state.companyId;
    return headers;
  } catch {
    return {};
  }
}

/**
 * Reads the response body once and returns parsed JSON when possible,
 * otherwise the raw text (or `null` for an empty body). Never throws —
 * malformed JSON falls back to the raw text so error reporting still works.
 */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '');
  if (!text) return null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  // Some backends return JSON without the proper header — best-effort parse.
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

function extractMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    // Nested envelope: { error: { code, message } } / { error: { detail } }
    const nested = obj.error;
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>;
      for (const key of ['message', 'detail', 'title'] as const) {
        const v = n[key];
        if (typeof v === 'string' && v.length > 0) return v;
      }
    }
    for (const key of ['message', 'error', 'detail', 'title'] as const) {
      const v = obj[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  if (typeof body === 'string' && body.length > 0 && body.length < 500) {
    return body;
  }
  return `Request failed with status ${status}`;
}

/**
 * A 401 on these is a failed sign-in attempt to report, not an expired session
 * to renew — refreshing there would spend the refresh token on every mistyped
 * login code. `/refresh-token` itself is excluded for the obvious reason.
 */
function isAuthEndpoint(path: string): boolean {
  return path.startsWith('/authentication/');
}

export interface ApiFetchInit extends RequestInit {
  /** Send as the signed-in operator; see `getAuthHeaders`. */
  asOperator?: boolean;
}

export async function apiFetch<T>(
  path: string,
  init: ApiFetchInit = {},
  baseUrl = API_BASE_URL,
  /** Internal: set on the single retry that follows a successful refresh. */
  isRetry = false,
): Promise<T> {
  const url = `${baseUrl}${path}`;
  const { asOperator, ...requestInit } = init;
  const res = await fetch(url, {
    // App data must reflect the server's current state — never serve a
    // stale HTTP cache hit (e.g. dashboard counts after the user just
    // created a new record). Callers can still opt in by passing
    // `cache: 'default'` explicitly.
    cache: 'no-store',
    credentials: 'include',
    ...requestInit,
    headers: {
      ...(requestInit.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
      'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...getAuthHeaders({ asOperator }),
      ...requestInit.headers,
    },
  });

  if (res.status === 401) {
    // The access token lives 24h, the refresh token 30d. Spend the second to
    // renew the first before concluding the session is over — skipping this is
    // what forced a fresh login every single day. Concurrent 401s share one
    // refresh (see `refreshAccessToken`), and the retry never refreshes again,
    // so a genuinely dead session costs exactly one extra round trip.
    if (!isRetry && !isAuthEndpoint(path) && hasRefreshToken()) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return apiFetch<T>(path, init, baseUrl, true);
    }

    localStorage.removeItem('retraced:context');
    // Docs are public — never bounce a docs visitor to login on a 401. Nor
    // bounce the login screen itself (`/`): `/authentication/*` answering 401
    // is a failed attempt to report, not an expired session to flee.
    const { pathname } = window.location;
    if (!pathname.startsWith('/docs') && pathname !== '/') {
      window.location.href = '/';
    }
    // Throw to abort the in-flight call; the redirect (if any) is scheduled.
    throw new ApiError('Session expired', { status: 401, body: null, url });
  }

  if (!res.ok) {
    const body = await readBody(res);
    throw new ApiError(extractMessage(body, res.status), {
      status: res.status,
      body,
      url,
    });
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}
